import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate, formatDisplayDateTime } from "../../utils/dateFormat";
import { RefreshCw, Search, Trash2, Calendar, Users, Building2, CreditCard, FileText, Filter, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PageInfoInline } from "./PageInfo";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Source badge configuration
const SOURCE_CONFIG = {
  "PAYMENT_LINK": { label: "Payment Link", color: "bg-purple-100 text-purple-800", icon: CreditCard },
  "BOOKING_FORM": { label: "Booking Form", color: "bg-blue-100 text-blue-800", icon: FileText },
  "form": { label: "Booking Form", color: "bg-blue-100 text-blue-800", icon: FileText },
  "ical": { label: "iCal", color: "bg-green-100 text-green-800", icon: Calendar },
  "ICAL": { label: "iCal", color: "bg-green-100 text-green-800", icon: Calendar },
  "manual": { label: "Manuale", color: "bg-gray-100 text-gray-800", icon: Users },
  "booking": { label: "Booking", color: "bg-amber-100 text-amber-800", icon: Building2 },
};

const SourceBadge = ({ source, platform }) => {
  const config = SOURCE_CONFIG[source] || { label: source || "Sconosciuto", color: "bg-gray-100 text-gray-800", icon: FileText };
  const Icon = config.icon;
  
  // For iCal, show platform name if available
  const displayLabel = (source === "ical" || source === "ICAL") && platform 
    ? platform 
    : config.label;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon size={12} />
      {displayLabel}
    </span>
  );
};

export default function UpdatePage({ getAuthHeaders, onMarkSeen }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Fetch all bookings on mount and mark as seen
  useEffect(() => {
    fetchBookings();
    markAsSeen();
  }, []);
  
  // Mark updates as seen
  const markAsSeen = async () => {
    try {
      await axios.post(`${API}/admin/updates/mark-seen`, {}, getAuthHeaders());
      // Notify parent to refresh badge count
      if (onMarkSeen) onMarkSeen();
    } catch (error) {
      console.error("Failed to mark updates as seen:", error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/updates/bookings`, getAuthHeaders());
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  };

  // Calculate nights
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    try {
      const ci = new Date(checkIn);
      const co = new Date(checkOut);
      return Math.ceil((co - ci) / (1000 * 60 * 60 * 24));
    } catch {
      return 0;
    }
  };

  // Format date - use centralized utility
  const formatDateDisplay = (dateStr) => {
    return formatDisplayDate(dateStr);
  };

  // Get total guests
  const getTotalGuests = (booking) => {
    const adults = booking.adults || 0;
    const children = booking.children || 0;
    const infants = booking.infants || 0;
    return adults + children + infants;
  };

  // Get guest details string
  const getGuestDetails = (booking) => {
    const parts = [];
    if (booking.adults) parts.push(`${booking.adults} adulti`);
    if (booking.children) {
      let childrenStr = `${booking.children} bambini`;
      if (booking.children_ages) {
        childrenStr += ` (${booking.children_ages})`;
      }
      parts.push(childrenStr);
    }
    if (booking.infants) parts.push(`${booking.infants} infanti`);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    // Source filter
    if (sourceFilter !== "all") {
      const bookingSource = (booking.source || "").toLowerCase();
      if (sourceFilter === "form" && bookingSource !== "booking_form" && bookingSource !== "form") return false;
      if (sourceFilter === "payment_link" && bookingSource !== "payment_link") return false;
      if (sourceFilter === "ical" && bookingSource !== "ical") return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = (booking.name || booking.guest_name || "").toLowerCase();
      const platform = (booking.platform || "").toLowerCase();
      const bookingNumber = (booking.booking_number || "").toLowerCase();
      return name.includes(query) || platform.includes(query) || bookingNumber.includes(query);
    }
    
    return true;
  });

  // Toggle booking selection
  const toggleBooking = (bookingId) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  // Toggle all bookings
  const toggleAllBookings = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id || b.request_id || b.booking_number));
    }
  };

  // Delete single booking
  const deleteBooking = async (booking) => {
    setDeletingBooking(booking);
    setDeletingBulk(false);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteBooking = async () => {
    const booking = deletingBooking;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/updates/bookings/${booking.id || booking.request_id}`, {
        ...getAuthHeaders(),
        data: { source: booking.source }
      });
      toast.success("Prenotazione eliminata definitivamente");
      fetchBookings();
      setSelectedBookings([]);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingBooking(null);
    }
  };

  // Delete selected bookings
  const deleteSelectedBookings = async () => {
    setDeletingBooking(null);
    setDeletingBulk(true);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteSelectedBookings = async () => {
    setDeleting(true);
    try {
      // Get the full booking objects for the selected IDs
      const bookingsToDelete = filteredBookings.filter(b => {
        const bId = b.id || b.request_id || b.booking_number;
        return selectedBookings.includes(bId);
      });
      
      // If no bookings found via filter, use selectedBookings directly with source from first match
      const bookingsData = bookingsToDelete.length > 0 
        ? bookingsToDelete.map(b => ({
            id: b.id || b.request_id,
            source: b.source
          }))
        : selectedBookings.map(id => {
            // Try to find the booking to get source
            const found = filteredBookings.find(b => 
              (b.id || b.request_id || b.booking_number) === id
            );
            return {
              id: id,
              source: found?.source || (id.startsWith('ical_') ? 'iCal' : 'unknown')
            };
          });
      
      console.log('Deleting bookings:', bookingsData);
      
      await axios.delete(`${API}/admin/updates/bookings/bulk`, {
        ...getAuthHeaders(),
        data: { bookings: bookingsData }
      });
      toast.success(`${selectedBookings.length} prenotazioni eliminate definitivamente`);
      fetchBookings();
      setSelectedBookings([]);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Errore nell'eliminazione: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingBulk(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl">Update</h1>
        <PageInfoInline pageKey="update" />
      </div>
      
      <div className="bg-white border border-[#D4AF37] p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-xl">Aggiornamenti Prenotazioni</h2>
            {filteredBookings.length > 0 && (
              <span className="text-sm text-[#78716C]">
                ({filteredBookings.length} {filteredBookings.length === 1 ? "risultato" : "risultati"})
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {selectedBookings.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={deleteSelectedBookings}
                disabled={deleting}
              >
                <Trash2 size={14} className="mr-1" /> 
                Elimina {selectedBookings.length} selezionati
              </Button>
            )}
            <Button variant="outline" onClick={fetchBookings} disabled={loading}>
              <RefreshCw size={16} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A8A29E]" />
            <Input 
              placeholder="Cerca per nome, piattaforma, numero prenotazione..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Source filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-[#78716C]" />
            <select 
              value={sourceFilter} 
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-10 px-3 border border-[#D4AF37] bg-white rounded-none"
            >
              <option value="all">Tutte le fonti</option>
              <option value="form">Da Form</option>
              <option value="payment_link">Da Payment Link</option>
              <option value="ical">Da iCal</option>
            </select>
          </div>
        </div>
        
        {/* Select all */}
        {filteredBookings.length > 0 && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-[#E7E5E4]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0} 
                onChange={toggleAllBookings}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-sm text-[#78716C]">
                Seleziona tutti ({filteredBookings.length})
              </span>
            </label>
          </div>
        )}
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={32} className="animate-spin text-[#D4AF37]" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-[#78716C]">
            <Calendar size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">Nessuna prenotazione trovata</p>
            <p className="text-sm">Le nuove prenotazioni appariranno qui automaticamente.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4AF37]">
                  <th className="w-10 py-3 px-2"></th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Fonte</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Nominativo</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Check-in</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Check-out</th>
                  <th className="text-center py-3 px-4 font-medium text-[#44403C]">Notti</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Ospiti</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      Rilevato il
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-[#44403C]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => {
                  const bookingId = booking.id || booking.request_id || booking.booking_number;
                  const isIcal = (booking.source || "").toLowerCase() === "ical";
                  const nights = calculateNights(booking.check_in, booking.check_out);
                  
                  return (
                    <tr 
                      key={bookingId} 
                      className={`border-b border-[#E7E5E4] hover:bg-[#FAFAF9] ${selectedBookings.includes(bookingId) ? 'bg-[#FDF9EF]' : ''}`}
                    >
                      <td className="py-3 px-2">
                        <input 
                          type="checkbox" 
                          checked={selectedBookings.includes(bookingId)} 
                          onChange={() => toggleBooking(bookingId)} 
                          className="w-4 h-4 accent-[#D4AF37]" 
                        />
                      </td>
                      <td className="py-3 px-4">
                        <SourceBadge source={booking.source} platform={booking.platform} />
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-[#1C1917]">
                            {booking.name || booking.guest_name || (isIcal ? booking.platform : "-")}
                          </div>
                          {booking.booking_number && (
                            <div className="text-xs text-[#78716C]">{booking.booking_number}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#44403C]">
                        {formatDateDisplay(booking.check_in)}
                      </td>
                      <td className="py-3 px-4 text-[#44403C]">
                        {formatDateDisplay(booking.check_out)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-medium text-[#1C1917]">{nights}</span>
                      </td>
                      <td className="py-3 px-4">
                        {isIcal ? (
                          <span className="text-[#78716C] text-sm">-</span>
                        ) : (
                          <div>
                            <div className="font-medium text-[#1C1917]">
                              {getTotalGuests(booking)} ospiti
                            </div>
                            <div className="text-xs text-[#78716C]">
                              {getGuestDetails(booking)}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[#44403C]">
                        {booking.imported_at ? (
                          <div className="text-sm">
                            {formatDisplayDateTime(booking.imported_at)}
                          </div>
                        ) : (
                          <span className="text-[#A8A29E]">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteBooking(booking)}
                          disabled={deleting}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Elimina definitivamente"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deletingBulk ? "Elimina Prenotazioni Selezionate" : "Elimina Prenotazione"}
        description={deletingBulk 
          ? `Stai per eliminare ${selectedBookings.length} prenotazioni e tutti i dati associati (date bloccate, note, etc.).`
          : `Stai per eliminare la prenotazione "${deletingBooking?.name || ''}" e tutti i dati associati.`
        }
        itemCount={deletingBulk ? `${selectedBookings.length} prenotazioni selezionate` : null}
        onConfirm={deletingBulk ? confirmDeleteSelectedBookings : confirmDeleteBooking}
        loading={deleting}
      />
    </div>
  );
}
